import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import VideoPlayer from '../components/VideoPlayer';

function formatDuration(seconds) {
  if (!seconds) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function formatBytes(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function PlaylistsPage() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const { id } = useParams();
  
  // Playlist list view state
  const [playlists, setPlaylists] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [newPlaylistDescription, setNewPlaylistDescription] = useState('');
  
  // Single playlist view state
  const [currentPlaylist, setCurrentPlaylist] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [playlistMode, setPlaylistMode] = useState(false); // true when playing from playlist
  const [libraryVideos, setLibraryVideos] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addSelected, setAddSelected] = useState([]);
  const [addLoading, setAddLoading] = useState(false);
  
  const [storageInfo, setStorageInfo] = useState({ used: 0, limit: 5 * 1024 * 1024 * 1024 });

  useEffect(() => {
    if (user) {
      setStorageInfo({
        used: user.storage_used_bytes || 0,
        limit: user.storage_limit_mb * 1024 * 1024
      });
      fetchPlaylists();
    }
  }, [user]);

  const fetchPlaylists = async () => {
    try {
      const response = await fetch('/api/playlists', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPlaylists(data);
      }
    } catch (err) {
      console.error('Failed to fetch playlists:', err);
    }
  };

  const handleCreatePlaylist = async (e) => {
    e.preventDefault();
    if (!newPlaylistName.trim()) return;

    try {
      const response = await fetch('/api/playlists', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: newPlaylistName,
          description: newPlaylistDescription
        })
      });

      if (response.ok) {
        setNewPlaylistName('');
        setNewPlaylistDescription('');
        setShowCreateModal(false);
        fetchPlaylists();
      }
    } catch (err) {
      console.error('Failed to create playlist:', err);
    }
  };

  const handleDeletePlaylist = async (playlistId, name) => {
    if (!confirm(`Delete playlist "${name}"?`)) return;

    try {
      const response = await fetch(`/api/playlists/${playlistId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        fetchPlaylists();
      }
    } catch (err) {
      console.error('Failed to delete playlist:', err);
    }
  };

  const handleOpenPlaylist = async (playlistId) => {
    try {
      const response = await fetch(`/api/playlists/${playlistId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setCurrentPlaylist(data);
      }
    } catch (err) {
      console.error('Failed to fetch playlist:', err);
    }
  };

  const handleRemoveFromPlaylist = async (playlistId, downloadId) => {
    try {
      const response = await fetch(`/api/playlists/${playlistId}/videos/${downloadId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        // Refresh the current playlist view
        if (currentPlaylist && currentPlaylist.id === playlistId) {
          handleOpenPlaylist(playlistId);
        }
      }
    } catch (err) {
      console.error('Failed to remove video:', err);
    }
  };

  const fetchLibraryVideos = async () => {
    try {
      const response = await fetch('/api/downloads', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setLibraryVideos(data);
      }
    } catch (err) {
      console.error('Failed to fetch library videos:', err);
    }
  };

  const handleAddVideos = async () => {
    if (addSelected.length === 0 || !currentPlaylist) return;
    setAddLoading(true);
    try {
      const response = await fetch(`/api/playlists/${currentPlaylist.id}/videos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ videoIds: addSelected })
      });
      if (response.ok) {
        setAddSelected([]);
        setShowAddModal(false);
        handleOpenPlaylist(currentPlaylist.id);
      }
    } catch (err) {
      console.error('Failed to add videos:', err);
    } finally {
      setAddLoading(false);
    }
  };

  const toggleAddSelect = (id) => {
    setAddSelected(prev =>
      prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id]
    );
  };

  const handleReorder = async (dragIndex, dropIndex) => {
    const reordered = [...currentPlaylist.videos];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(dropIndex, 0, moved);
    setCurrentPlaylist({ ...currentPlaylist, videos: reordered });
    try {
      await fetch(`/api/playlists/${currentPlaylist.id}/videos/reorder`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ videoIds: reordered.map(v => v.id) })
      });
    } catch (err) {
      console.error('Failed to reorder:', err);
      handleOpenPlaylist(currentPlaylist.id);
    }
  };

  const storagePercent = Math.min((storageInfo.used / storageInfo.limit) * 100, 100);

  if (currentPlaylist) {
    // Single playlist view
    return (
      <div className="dashboard">
        <nav className="navbar">
          <div className="navbar-brand">
            <h1>PLAYLIST <span>{currentPlaylist.name}</span></h1>
          </div>
          <div className="navbar-right">
            <div className="storage-indicator">
              <span>Storage</span>
              <div className="storage-bar">
                <div className="bar-fill" style={{ width: `${storagePercent}%` }} />
              </div>
              <span>{formatBytes(storageInfo.used)} / {formatBytes(storageInfo.limit)}</span>
            </div>
            <span className="user-info">{user?.username}</span>
            <div className="user-actions">
              <button className="btn-secondary" onClick={() => setCurrentPlaylist(null)}>
                Back to Playlists
              </button>
              <button className="btn-secondary" onClick={logout}>
                Logout
              </button>
            </div>
          </div>
        </nav>

        <main className="main-content">
          <section className="video-section">
            <div className="section-header">
              <h2>{currentPlaylist.name}</h2>
              <span className="number">{currentPlaylist.videos.length} videos</span>
              <button
                className="btn-secondary"
                onClick={() => {
                  fetchLibraryVideos();
                  setShowAddModal(true);
                }}
                style={{ marginLeft: 'auto', marginRight: '12px' }}
              >
                + Add Videos
              </button>
              {currentPlaylist.videos.length > 0 && (
                <button
                  className="btn-primary"
                  onClick={() => {
                    setPlaylistMode(true);
                    setSelectedVideo(currentPlaylist.videos[0]);
                  }}
                  style={{ marginRight: '12px' }}
                >
                  ▶ Play All
                </button>
              )}
            </div>
            
            {currentPlaylist.videos.length === 0 ? (
              <div className="empty-state">
                <h3>No videos in this playlist</h3>
                <p>Add videos from your library</p>
              </div>
            ) : (
              <div className="playlist-video-list">
                {currentPlaylist.videos.map((video, index) => (
                  <div
                    key={video.id}
                    className="playlist-video-item"
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.setData('text/plain', index.toString());
                      e.currentTarget.classList.add('dragging');
                    }}
                    onDragEnd={(e) => {
                      e.currentTarget.classList.remove('dragging');
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      const fromIndex = parseInt(e.dataTransfer.getData('text/plain'));
                      if (fromIndex !== index) handleReorder(fromIndex, index);
                    }}
                  >
                    <div className="drag-handle">⠿</div>
                    <div className="playlist-video-number">{index + 1}</div>
                    <div
                      className="playlist-video-thumbnail"
                      onClick={() => setSelectedVideo(video)}
                    >
                      {video.thumbnail_url ? (
                        <img
                          src={video.thumbnail_url}
                          alt={video.title}
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      ) : null}
                      <div className="play-overlay">
                        <div className="play-icon">▶</div>
                      </div>
                    </div>
                    <div className="playlist-video-info">
                      <h3>{video.title}</h3>
                      <div className="playlist-video-meta">
                        <span>{formatDuration(video.duration)}</span>
                        <span>{video.quality}</span>
                      </div>
                    </div>
                    <div className="playlist-video-actions">
                      <button
                        className="btn-action"
                        onClick={() => setSelectedVideo(video)}
                      >
                        Play
                      </button>
                      <button
                        className="btn-danger"
                        onClick={() => handleRemoveFromPlaylist(currentPlaylist.id, video.id)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </main>

        {selectedVideo && (
          <VideoPlayer
            key={selectedVideo.id}
            video={selectedVideo}
            playlist={playlistMode ? currentPlaylist.videos : null}
            currentIndex={playlistMode && currentPlaylist.videos ? currentPlaylist.videos.findIndex(v => v.id === selectedVideo.id) : null}
            onNext={() => {
              if (playlistMode && currentPlaylist.videos) {
                const idx = currentPlaylist.videos.findIndex(v => v.id === selectedVideo.id);
                if (idx >= 0 && idx < currentPlaylist.videos.length - 1) {
                  setSelectedVideo(currentPlaylist.videos[idx + 1]);
                }
              }
            }}
            onPrev={() => {
              if (playlistMode && currentPlaylist.videos) {
                const idx = currentPlaylist.videos.findIndex(v => v.id === selectedVideo.id);
                if (idx > 0) {
                  setSelectedVideo(currentPlaylist.videos[idx - 1]);
                }
              }
            }}
            onClose={() => {
              setSelectedVideo(null);
              setPlaylistMode(false);
            }}
          />
        )}

        {/* Add Videos modal */}
        {showAddModal && (
          <div className="modal-overlay" onClick={(e) => {
            if (e.target === e.currentTarget) setShowAddModal(false);
          }}>
            <button className="close-btn" onClick={() => setShowAddModal(false)}>×</button>
            <div className="modal-content add-videos-modal" style={{ maxWidth: '600px' }}>
              <h2>Add Videos to "{currentPlaylist.name}"</h2>
              <div style={{ marginBottom: '12px', fontSize: '13px', color: '#888' }}>
                {addSelected.length} selected
              </div>
              <div style={{ maxHeight: '400px', overflowY: 'auto', marginBottom: '16px' }}>
                {libraryVideos.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '24px', color: '#666' }}>
                    No videos in your library
                  </div>
                ) : (
                  libraryVideos.map((video) => {
                    const alreadyInPlaylist = currentPlaylist.videos.some(v => v.id === video.id);
                    return (
                      <div
                        key={video.id}
                        className={`add-video-item ${addSelected.includes(video.id) ? 'selected' : ''} ${alreadyInPlaylist ? 'disabled' : ''}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '10px 12px',
                          marginBottom: '4px',
                          borderRadius: '6px',
                          border: `1px solid ${addSelected.includes(video.id) ? '#c2ef4e' : '#333'}`,
                          background: addSelected.includes(video.id) ? 'rgba(194,239,78,0.1)' : 'transparent',
                          cursor: alreadyInPlaylist ? 'not-allowed' : 'pointer',
                          opacity: alreadyInPlaylist ? 0.4 : 1,
                        }}
                        onClick={() => !alreadyInPlaylist && toggleAddSelect(video.id)}
                      >
                        <input
                          type="checkbox"
                          checked={addSelected.includes(video.id)}
                          disabled={alreadyInPlaylist}
                          onChange={() => {}}
                          style={{ cursor: alreadyInPlaylist ? 'not-allowed' : 'pointer' }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '13px', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {video.title}
                          </div>
                          <div style={{ fontSize: '11px', color: '#888' }}>
                            {video.quality} • {formatDuration(video.duration)}
                            {alreadyInPlaylist && <span style={{ color: '#f9a825', marginLeft: '8px' }}>✓ Already in playlist</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button className="btn-secondary" onClick={() => setShowAddModal(false)}>Cancel</button>
                <button
                  className="btn-primary"
                  onClick={handleAddVideos}
                  disabled={addSelected.length === 0 || addLoading}
                >
                  {addLoading ? 'Adding...' : `Add ${addSelected.length} Video${addSelected.length !== 1 ? 's' : ''}`}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Playlist list view
  return (
    <div className="dashboard">
      <nav className="navbar">
        <div className="navbar-brand">
          <h1>MEDIA <span>SERVER</span></h1>
        </div>
        <div className="navbar-right">
          <div className="storage-indicator">
            <span>Storage</span>
            <div className="storage-bar">
              <div className="bar-fill" style={{ width: `${storagePercent}%` }} />
            </div>
            <span>{formatBytes(storageInfo.used)} / {formatBytes(storageInfo.limit)}</span>
          </div>
          <span className="user-info">{user?.username}</span>
          <div className="user-actions">
            <button className="btn-secondary" onClick={() => navigate('/')}>
              Library
            </button>
            <button className="btn-secondary" onClick={() => navigate('/gallery')}>
              Gallery
            </button>
            {user?.is_admin && (
              <button className="btn-admin" onClick={() => navigate('/x1n2t3r4n5l')}>
                Admin
              </button>
            )}
            <button className="btn-secondary" onClick={logout}>
              Logout
            </button>
          </div>
        </div>
      </nav>

      <main className="main-content">
        <section className="playlist-section">
          <div className="section-header">
            <h2>Your Playlists</h2>
            <span className="number">{playlists.length} playlists</span>
          </div>

          {playlists.length === 0 ? (
            <div className="empty-state">
              <h3>No playlists yet</h3>
              <p>Create your first playlist to organize your videos</p>
            </div>
          ) : (
            <div className="playlist-grid">
              {playlists.map((playlist) => (
                <div key={playlist.id} className="playlist-card">
                  <div 
                    className="playlist-thumbnail"
                    onClick={() => handleOpenPlaylist(playlist.id)}
                  >
                    <div className="playlist-icon">▶</div>
                  </div>
                  <div className="playlist-info">
                    <h3 onClick={() => handleOpenPlaylist(playlist.id)}>{playlist.name}</h3>
                    {playlist.description && (
                      <p className="playlist-description">{playlist.description}</p>
                    )}
                    <div className="playlist-meta">
                      <span>{playlist.video_count} videos</span>
                    </div>
                    <div className="playlist-actions">
                      <button 
                        className="btn-action"
                        onClick={() => handleOpenPlaylist(playlist.id)}
                      >
                        Open
                      </button>
                      <button 
                        className="btn-danger"
                        onClick={() => handleDeletePlaylist(playlist.id, playlist.name)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="create-playlist-section">
            <button 
              className="btn-primary" 
              onClick={() => setShowCreateModal(true)}
            >
              + Create Playlist
            </button>
          </div>
        </section>
      </main>

      {showCreateModal && (
        <div className="modal-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) setShowCreateModal(false);
        }}>
          <button className="close-btn" onClick={() => setShowCreateModal(false)}>×</button>
          <div className="modal-content create-playlist-modal">
            <h2>Create Playlist</h2>
            <form onSubmit={handleCreatePlaylist}>
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={newPlaylistName}
                  onChange={(e) => setNewPlaylistName(e.target.value)}
                  placeholder="My Playlist"
                  required
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>Description (optional)</label>
                <input
                  type="text"
                  value={newPlaylistDescription}
                  onChange={(e) => setNewPlaylistDescription(e.target.value)}
                  placeholder="A description..."
                />
              </div>
              <button type="submit" className="btn-primary">
                Create
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default PlaylistsPage;
